import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from './api';
import { BILLING_LABELS_SHORT, type BillingType } from './lib/billingType';
import { keyToNoonIso, torontoDayKey } from './lib/week';
import { SessionLogSkeleton } from './components/Skeleton';

interface SessionNote {
  at: string;
  text: string;
}

interface RunningSession {
  employeeId: string;
  dealId: string;
  startedAt: string;
  notes: SessionNote[];
  updatedAt: string;
  billingType: BillingType;
}

// BillingTypeSelector removed (Tim's trial feedback: logging is hourly-only).
// The billingType enum, flat-rate machinery, and legacy flat entries are
// untouched — only the picker UI is gone.

interface SessionConflict {
  error: 'session_running';
  current: RunningSession;
}

interface SessionLogProps {
  dealId: string;
  employeeId: string;
  dealLabel: string;
  // Billing types that resolve as flat for this employee. When the active
  // billing type is in this list the session UI hides the stopwatch — flat
  // entries aren't time-bound, the server derives minutes from the rate.
  flatBillingTypes: string[];
  // Back-office employees (Ashley) log hourly work by typing start/end
  // datetimes instead of running a stopwatch. Flat-rate types ignore this
  // — those stay unit-based for everyone.
  prefersTimeRange: boolean;
  // Called after a successful submit so the parent can clear the selected
  // job — Tim's trial feedback: the form should reset completely, job
  // included, so stale selections don't collect the next entry.
  onLogged?: () => void;
}

const MAX_SESSION_MINUTES = 24 * 60;
const TICK_MS = 1000;
const REFETCH_MS = 30_000;

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function isConflict(error: unknown): error is SessionConflict {
  if (!(error instanceof Error)) return false;
  // apiFetch throws Error with `${status} ${body}` shape.
  if (!error.message.startsWith('409 ')) return false;
  try {
    const body = JSON.parse(error.message.slice(4)) as unknown;
    return (
      typeof body === 'object' && body !== null &&
      (body as { error?: unknown }).error === 'session_running'
    );
  } catch {
    return false;
  }
}

function parseConflict(error: unknown): SessionConflict | null {
  if (!(error instanceof Error)) return null;
  if (!error.message.startsWith('409 ')) return null;
  try {
    return JSON.parse(error.message.slice(4)) as SessionConflict;
  } catch {
    return null;
  }
}

const noteTimeFmt = new Intl.DateTimeFormat('en-CA', {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

export function SessionLog({ dealId, employeeId, dealLabel, flatBillingTypes, prefersTimeRange, onLogged }: SessionLogProps): JSX.Element {
  const flatSet = useMemo(() => new Set(flatBillingTypes), [flatBillingTypes]);
  const qc = useQueryClient();
  const sessionKey = useMemo<readonly unknown[]>(
    () => ['running-session', employeeId],
    [employeeId],
  );

  // PR 0007 — server resolves employee from JWT. employeeId prop is kept
  // for cache keys + display but no longer travels in the wire body.
  const sessionQuery = useQuery<RunningSession | null>({
    queryKey: sessionKey,
    queryFn: () => apiFetch<RunningSession | null>(`/running-session`),
    refetchInterval: REFETCH_MS,
  });

  const session = sessionQuery.data ?? null;

  const [draft, setDraft] = useState<string>('');
  const [now, setNow] = useState<number>(() => Date.now());
  const [confirmEndOpen, setConfirmEndOpen] = useState<boolean>(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState<boolean>(false);
  // Pre-start billing-type selection. Once a session is running, the source of
  // truth is `session.billingType` (the server row), not this state.
  const [pendingBillingType, setPendingBillingType] = useState<BillingType>('hourly');
  const noteInputRef = useRef<HTMLInputElement | null>(null);

  // Reset transient UI state when employee changes (different person logging in).
  useEffect(() => {
    setDraft('');
    setConfirmEndOpen(false);
    setConfirmCancelOpen(false);
    setPendingBillingType('hourly');
  }, [employeeId]);

  // Tick the clock while a session is active.
  useEffect(() => {
    if (!session) return;
    const t = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(t);
  }, [session]);

  const elapsedMs = useMemo<number>(() => {
    if (!session) return 0;
    return now - new Date(session.startedAt).getTime();
  }, [session, now]);

  const elapsedMinutes = Math.max(0, Math.round(elapsedMs / 60_000));
  const overLimit = elapsedMinutes > MAX_SESSION_MINUTES;

  const start = useMutation({
    mutationFn: () =>
      apiFetch<RunningSession>('/running-session/start', {
        method: 'POST',
        body: JSON.stringify({ dealId, billingType: pendingBillingType }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(sessionKey, data);
      setNow(Date.now());
    },
  });

  // Tim's final-feedback item 1: most entries are "N hours on this job on
  // this date" — a number box beats picking start/end times. 'hours' is
  // therefore the default mode; 'range' stays for anyone who wants clock
  // precision (and its entries keep the overlap guard, since only ranges
  // carry ended_at).
  const [logMode, setLogMode] = useState<'hours' | 'range'>('hours');
  const [hoursDate, setHoursDate] = useState<string>(() => torontoDayKey(new Date()));
  const [hoursAmount, setHoursAmount] = useState<string>('');
  const [hoursNote, setHoursNote] = useState<string>('');
  const hoursParsed = Number(hoursAmount);
  const hoursValid = Number.isFinite(hoursParsed) && hoursParsed > 0 && hoursParsed <= 24;

  const hoursSubmit = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string }>('/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          dealId,
          billingType: pendingBillingType,
          minutes: Math.round(hoursParsed * 60),
          // Noon-stamp, same convention as the week-grid quick-add: the entry
          // lands on the intended Toronto calendar day regardless of browser tz.
          startedAt: keyToNoonIso(hoursDate),
          ...(hoursNote.trim() ? { note: hoursNote.trim() } : {}),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['week'] });
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      qc.invalidateQueries({ queryKey: ['admin', 'jobs-summary'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      setHoursAmount('');
      setHoursNote('');
      setHoursDate(torontoDayKey(new Date()));
      onLogged?.();
    },
  });

  // Time-range submit path. Server derives minutes from (endedAt - startedAt).
  // Both inputs start empty — pre-filling Start with "now" mis-leads the
  // after-the-fact use case (log work that already happened earlier today),
  // because typing End < Start silently disables submit with no explanation.
  const [rangeStart, setRangeStart] = useState<string>('');
  const [rangeEnd, setRangeEnd] = useState<string>('');
  const [rangeNote, setRangeNote] = useState<string>('');
  const rangeOutOfOrder = Boolean(
    rangeStart && rangeEnd && new Date(rangeEnd) < new Date(rangeStart),
  );
  // End is prefilled with Start's value when Start is picked first, so
  // equality is the expected intermediate state — guide, don't scold.
  const rangeZeroLength = Boolean(
    rangeStart && rangeEnd && rangeEnd === rangeStart,
  );
  // Safari's empty <input type="datetime-local"> renders a grey "06/09/2026,
  // 12:30 PM"-shaped hint that reads as a real value — the user thinks End
  // is set, taps submit, and the button silently does nothing. Surface what's
  // missing inline so the disabled state isn't a guessing game.
  const rangeHint: string | null = rangeOutOfOrder
    ? 'End time must be after start time.'
    : rangeZeroLength
      ? 'Now set the end time.'
      : rangeStart && !rangeEnd
        ? 'Pick an end time to enable submit.'
        : rangeEnd && !rangeStart
          ? 'Pick a start time to enable submit.'
          : null;
  const rangeSubmit = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string }>('/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          dealId,
          billingType: pendingBillingType,
          startedAt: new Date(rangeStart).toISOString(),
          endedAt: new Date(rangeEnd).toISOString(),
          ...(rangeNote.trim() ? { note: rangeNote.trim() } : {}),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['week'] });
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      qc.invalidateQueries({ queryKey: ['admin', 'jobs-summary'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      setRangeStart('');
      setRangeEnd('');
      setRangeNote('');
      onLogged?.();
    },
  });

  // Quick-submit for flat-rate work where the timer adds no info: the
  // server derives minutes from the rate sheet (flat_amount × 60 /
  // default_hourly_rate). One tap = one billable unit logged.
  const quickSubmit = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string }>('/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          dealId,
          billingType: pendingBillingType,
          unitCount: 1,
          quickLog: true,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['week'] });
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      qc.invalidateQueries({ queryKey: ['admin', 'jobs-summary'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      onLogged?.();
    },
  });

  const addNote = useMutation({
    mutationFn: (text: string) =>
      apiFetch<RunningSession>('/running-session/note', {
        method: 'POST',
        body: JSON.stringify({
          at: new Date().toISOString(),
          text,
        }),
      }),
    onMutate: async (text) => {
      await qc.cancelQueries({ queryKey: sessionKey });
      const previous = qc.getQueryData<RunningSession | null>(sessionKey) ?? null;
      if (previous) {
        const optimistic: RunningSession = {
          ...previous,
          notes: [...previous.notes, { at: new Date().toISOString(), text }],
          updatedAt: new Date().toISOString(),
        };
        qc.setQueryData(sessionKey, optimistic);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(sessionKey, ctx.previous);
      }
    },
    onSuccess: (data) => {
      qc.setQueryData(sessionKey, data);
      noteInputRef.current?.focus();
    },
  });

  const endSession = useMutation({
    mutationFn: () =>
      apiFetch<{ entry: { id: string }; noteIds: string[] }>(
        '/running-session/end',
        {
          method: 'POST',
          body: '{}',
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['week'] });
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      qc.invalidateQueries({ queryKey: ['admin', 'jobs-summary'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      qc.invalidateQueries({ queryKey: ['approvals', 'pending'] });
      qc.invalidateQueries({ queryKey: sessionKey });
      qc.setQueryData(sessionKey, null);
      setDraft('');
      setConfirmEndOpen(false);
      setPendingBillingType('hourly');
      onLogged?.();
    },
  });

  const cancelSession = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>('/running-session', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.setQueryData(sessionKey, null);
      qc.invalidateQueries({ queryKey: sessionKey });
      setDraft('');
      setConfirmCancelOpen(false);
    },
  });

  const onStart = useCallback((): void => {
    start.mutate();
  }, [start]);

  const onAddNote = useCallback((): void => {
    const text = draft.trim();
    if (!text) return;
    addNote.mutate(text);
    setDraft('');
  }, [draft, addNote]);

  const confirmEnd = useCallback((): void => {
    if (!session) return;
    const sessionIsFlat = flatSet.has(session.billingType);
    if (!sessionIsFlat && (elapsedMinutes <= 0 || overLimit)) return;
    endSession.mutate();
  }, [session, flatSet, elapsedMinutes, overLimit, endSession]);

  const confirmCancel = useCallback((): void => {
    cancelSession.mutate();
  }, [cancelSession]);

  if (sessionQuery.isLoading) {
    return <SessionLogSkeleton />;
  }

  if (sessionQuery.isError) {
    return (
      <section className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-brand-charcoal">Session</h1>
          <p className="text-xs text-red-600">
            Couldn't load session.{' '}
            <button
              type="button"
              onClick={() => sessionQuery.refetch()}
              className="underline"
            >
              Retry
            </button>
          </p>
        </div>
      </section>
    );
  }

  // Idle — but the start mutation may have failed with a conflict on another deal.
  if (!session) {
    const conflict = isConflict(start.error) ? parseConflict(start.error) : null;
    return (
      <section className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-brand-charcoal">Session</h1>
          <p className="text-xs text-brand-slate">
            <span className="text-brand-charcoal font-medium">{dealLabel}</span>
          </p>
        </div>

        {conflict && (
          <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-300">
            <p className="text-sm font-semibold text-amber-900">
              Another session is already running.
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Started{' '}
              {new Date(conflict.current.startedAt).toLocaleString()} on a
              different job. End it first, then start a new one here.
            </p>
          </div>
        )}

        {/* Billing-type picker removed (Tim's trial feedback: "just one
            hourly thing"). pendingBillingType is pinned to 'hourly'; the
            flat/quick path below stays for the unlikely case an hourly
            rate cell is configured flat. */}
        {flatSet.has(pendingBillingType) ? (
          // Flat-rate work isn't time-bound — no timer path. Submit is the
          // only action, with the same primary styling Start session had.
          <button
            type="button"
            onClick={() => quickSubmit.mutate()}
            disabled={quickSubmit.isPending}
            className="min-h-[56px] rounded-xl bg-brand-blue px-6 py-4 text-base font-semibold text-brand-white hover:bg-[#0067BB] active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {quickSubmit.isPending ? 'Logging…' : `Submit ${BILLING_LABELS_SHORT[pendingBillingType]}`}
          </button>
        ) : prefersTimeRange ? (
          // Typed-entry path. Two modes: 'hours' (date + number, the fast
          // default) and 'range' (start/end clock times).
          <div className="flex flex-col gap-3">
            <div role="radiogroup" aria-label="Entry mode" className="grid grid-cols-2 gap-2">
              {(['hours', 'range'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={logMode === m}
                  onClick={() => setLogMode(m)}
                  className={
                    'min-h-[40px] rounded-lg px-2 py-2 text-xs font-semibold active:scale-95 transition-transform ' +
                    (logMode === m
                      ? 'bg-brand-blue text-brand-white ring-1 ring-brand-blue'
                      : 'bg-brand-mist text-brand-charcoal ring-1 ring-brand-steel hover:bg-brand-steel')
                  }
                >
                  {m === 'hours' ? 'Hours' : 'Start & end'}
                </button>
              ))}
            </div>

            {logMode === 'hours' ? (
              <>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Date</span>
                  <input
                    type="date"
                    value={hoursDate}
                    onChange={(e) => setHoursDate(e.target.value)}
                    className="min-h-[44px] w-full min-w-0 rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Hours</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0.25}
                    max={24}
                    step={0.25}
                    value={hoursAmount}
                    onChange={(e) => setHoursAmount(e.target.value)}
                    placeholder="e.g. 8"
                    className="min-h-[44px] w-full min-w-0 rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Note (optional)</span>
                  <input
                    type="text"
                    value={hoursNote}
                    onChange={(e) => setHoursNote(e.target.value)}
                    placeholder="e.g. drafting + revisions"
                    className="min-h-[44px] rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                  />
                </label>
                {hoursAmount && !hoursValid && (
                  <p className="text-sm text-red-600">Hours must be between 0.25 and 24.</p>
                )}
                <button
                  type="button"
                  onClick={() => hoursSubmit.mutate()}
                  disabled={hoursSubmit.isPending || !hoursDate || !hoursValid}
                  className="min-h-[56px] rounded-xl bg-brand-blue px-6 py-4 text-base font-semibold text-brand-white hover:bg-[#0067BB] active:scale-[0.98] transition-transform disabled:opacity-50"
                >
                  {hoursSubmit.isPending
                    ? 'Logging…'
                    : hoursValid
                      ? `Submit ${hoursParsed}h`
                      : 'Submit hours'}
                </button>
                {hoursSubmit.isError && (
                  <p className="text-sm text-red-600">
                    Couldn&rsquo;t log:{' '}
                    {hoursSubmit.error instanceof Error ? hoursSubmit.error.message : 'Unknown error.'}
                  </p>
                )}
                {hoursSubmit.isSuccess && (
                  <p className="text-sm text-emerald-700">Logged.</p>
                )}
              </>
            ) : (
              <>
            {/* Start/End stack vertically, never side-by-side. In the narrow
                left column (320–400px on lg) a 2-col grid left each input
                ~170px wide — narrower than the intrinsic width of Safari's
                datetime-local field, so WebKit clipped the trailing AM/PM
                segment clean off and users could only enter 1:00–12:59
                (Tim's "I cannot post time for the afternoons"). Full-width
                inputs always have room for the AM/PM segment. */}
            <div className="grid grid-cols-1 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Start</span>
                <input
                  type="datetime-local"
                  value={rangeStart}
                  onChange={(e) => {
                    const next = e.target.value;
                    setRangeStart(next);
                    // Tim's trial feedback: End should assume the same day as
                    // Start. Prefill End with Start's full value so only the
                    // time segment needs adjusting; never clobber an End the
                    // user already picked.
                    if (next && !rangeEnd) setRangeEnd(next);
                  }}
                  className="min-h-[44px] w-full min-w-0 rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">End</span>
                <input
                  type="datetime-local"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="min-h-[44px] w-full min-w-0 rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                />
              </label>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">Note (optional)</span>
              <input
                type="text"
                value={rangeNote}
                onChange={(e) => setRangeNote(e.target.value)}
                placeholder="e.g. drafting + revisions"
                className="min-h-[44px] rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
              />
            </label>
            {rangeHint && (
              <p className={'text-sm ' + (rangeOutOfOrder ? 'text-red-600' : 'text-brand-slate')}>
                {rangeHint}
              </p>
            )}
            <button
              type="button"
              onClick={() => rangeSubmit.mutate()}
              disabled={
                rangeSubmit.isPending
                || !rangeStart
                || !rangeEnd
                || rangeOutOfOrder
                || rangeZeroLength
              }
              className="min-h-[56px] rounded-xl bg-brand-blue px-6 py-4 text-base font-semibold text-brand-white hover:bg-[#0067BB] active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              {rangeSubmit.isPending ? 'Logging…' : 'Submit hours'}
            </button>
            {rangeSubmit.isError && (
              <p className="text-sm text-red-600">
                {rangeSubmit.error instanceof Error && rangeSubmit.error.message.includes('"overlap"')
                  ? 'This range overlaps hours you already logged for this day. Check Recent entries and adjust the times.'
                  : `Couldn't log: ${rangeSubmit.error instanceof Error ? rangeSubmit.error.message : 'Unknown error.'}`}
              </p>
            )}
            {rangeSubmit.isSuccess && (
              <p className="text-sm text-emerald-700">Logged.</p>
            )}
              </>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onStart}
            disabled={start.isPending}
            className="min-h-[56px] rounded-xl bg-brand-blue px-6 py-4 text-base font-semibold text-brand-white hover:bg-[#0067BB] active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {start.isPending ? 'Starting…' : 'Start session'}
          </button>
        )}

        {start.isError && !conflict && (
          <p className="text-sm text-red-600">
            Failed to start:{' '}
            {start.error instanceof Error ? start.error.message : 'Unknown error'}.
          </p>
        )}
        {quickSubmit.isError && (
          <p className="text-sm text-red-600">
            Couldn't log:{' '}
            {quickSubmit.error instanceof Error && quickSubmit.error.message.includes('minutes_required')
              ? 'no flat rate configured for this billing type — ask Tim to set one in /admin/rates.'
              : (quickSubmit.error instanceof Error ? quickSubmit.error.message : 'Unknown error.')}
          </p>
        )}
        {quickSubmit.isSuccess && (
          <p className="text-sm text-emerald-700">Logged.</p>
        )}

        <p className="text-xs text-brand-slate">
          {flatSet.has(pendingBillingType)
            ? 'One tap logs the unit. No timer needed for flat-rate work.'
            : prefersTimeRange
              ? logMode === 'hours'
                ? 'Pick the date, type the hours, submit. Switch to Start & end for exact clock times.'
                : 'Enter the start and end of your work session and submit.'
              : "Hit start when you're on the job. Add notes as you work."}
        </p>
      </section>
    );
  }

  // Running state — but this device may be viewing a different deal than what's running.
  const runningOnAnotherDeal = session.dealId !== dealId;
  const isFlatActive = flatSet.has(session.billingType);
  // Flat sessions skip the elapsed-time gates: the server derives minutes
  // from (flat_amount × 60 / default_hourly_rate), so a 2-second session
  // and a 2-hour one log the same minutes. End is allowed at any time.
  const submitDisabled = isFlatActive
    ? endSession.isPending
    : elapsedMinutes <= 0 || overLimit || endSession.isPending;

  return (
    <section className="flex flex-col gap-4 py-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-brand-charcoal">Session</h1>
        <p className="text-xs text-brand-slate">
          <span className="text-brand-charcoal font-medium">{dealLabel}</span>
        </p>
      </div>

      {runningOnAnotherDeal && (
        <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-300">
          <p className="text-sm font-semibold text-amber-900">
            Running on a different job
          </p>
          <p className="mt-1 text-xs text-amber-800">
            This timer was started on another deal. End it before starting one
            here.
          </p>
        </div>
      )}

      {isFlatActive ? (
        <div className="flex flex-col gap-1 rounded-xl bg-brand-steel p-4 ring-1 ring-brand-steel">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-slate">
            Flat rate — not time bound
          </p>
          <p className="text-sm text-brand-charcoal">
            Logs one <span className="font-semibold">{BILLING_LABELS_SHORT[session.billingType]}</span> unit at the rate-sheet equivalent hours.
          </p>
          <p className="mt-1 text-xs text-brand-slate tabular-nums">
            Start {new Date(session.startedAt).toLocaleTimeString()} · End on submit
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1 rounded-xl bg-brand-steel p-4 ring-1 ring-brand-steel">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-slate">Running</p>
          <p className="font-mono text-5xl font-semibold tabular-nums text-brand-blue">
            {formatElapsed(elapsedMs)}
          </p>
          <p className="text-xs text-brand-slate">
            Started {new Date(session.startedAt).toLocaleTimeString()}
          </p>
          {overLimit && (
            <p className="mt-2 text-xs text-amber-700">
              Over 24h. End and start a new session.
            </p>
          )}
        </div>
      )}

      {/* Mid-session billing-type switcher removed with the picker —
          sessions are hourly. The /running-session/billing-type endpoint
          stays server-side for legacy flat sessions. */}
      <div className="flex flex-col gap-2">
        <label className="text-xs uppercase tracking-wide font-semibold text-brand-charcoal">Add a status note</label>
        <div className="flex gap-2">
          <input
            ref={noteInputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddNote();
              }
            }}
            placeholder="e.g. arrived on site"
            className="min-h-[44px] flex-1 rounded-lg bg-brand-white px-4 py-2 text-base text-brand-charcoal outline-none ring-1 ring-brand-steel placeholder:text-brand-slate/60 focus:ring-2 focus:ring-brand-blue"
          />
          <button
            type="button"
            onClick={onAddNote}
            disabled={!draft.trim() || addNote.isPending}
            className="min-h-[44px] rounded-lg bg-brand-charcoal px-4 py-2 text-sm font-semibold text-brand-white active:scale-95 transition-transform disabled:opacity-40"
          >
            {addNote.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
        {addNote.isError && (
          <p className="text-xs text-red-600">
            Note failed to save:{' '}
            {addNote.error instanceof Error ? addNote.error.message : 'Unknown error'}.
          </p>
        )}
      </div>

      {session.notes.length > 0 && (
        <ul className="flex flex-col gap-2">
          {session.notes.map((n) => (
            <li
              key={`${n.at}-${n.text}`}
              className="flex items-start gap-3 rounded-lg bg-brand-white px-4 py-3 ring-1 ring-brand-steel"
            >
              <span className="mt-0.5 font-mono text-[11px] tabular-nums text-brand-slate shrink-0">
                {noteTimeFmt.format(new Date(n.at))}
              </span>
              <span className="text-sm text-brand-charcoal">{n.text}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={() => setConfirmEndOpen(true)}
        disabled={submitDisabled}
        className={
          'min-h-[56px] rounded-xl px-6 py-4 text-base font-semibold text-brand-white active:scale-[0.98] transition-transform disabled:opacity-40 ' +
          (isFlatActive
            ? 'bg-brand-blue hover:bg-[#0067BB]'
            : 'bg-red-600 hover:bg-red-700')
        }
      >
        {isFlatActive ? 'Submit' : 'End session'}
      </button>

      <button
        type="button"
        onClick={() => setConfirmCancelOpen(true)}
        disabled={cancelSession.isPending}
        className="min-h-[36px] rounded-lg px-4 py-2 text-xs font-medium text-brand-slate hover:text-brand-charcoal disabled:opacity-40"
      >
        Cancel without logging
      </button>

      {endSession.isError && (
        <p className="text-sm text-red-600">
          Failed to log:{' '}
          {endSession.error instanceof Error ? endSession.error.message : 'Unknown error'}.
          Your session is safe — try again.
        </p>
      )}
      {cancelSession.isError && (
        <p className="text-sm text-red-600">
          Failed to cancel:{' '}
          {cancelSession.error instanceof Error ? cancelSession.error.message : 'Unknown error'}.
        </p>
      )}

      {confirmEndOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="end-session-title"
          className="fixed inset-0 z-50 flex items-end justify-center bg-brand-charcoal/60 p-4 sm:items-center"
          onClick={() => setConfirmEndOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-brand-white p-6 ring-1 ring-brand-steel shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="end-session-title" className="text-lg font-semibold text-brand-charcoal">
              {isFlatActive ? 'Submit this entry?' : 'End this session?'}
            </h2>
            <p className="mt-2 text-sm text-brand-slate">
              {isFlatActive ? (
                <>
                  Logs one{' '}
                  <span className="text-brand-charcoal font-medium">
                    {BILLING_LABELS_SHORT[session.billingType]}
                  </span>{' '}
                  at the rate-sheet equivalent hours, with {session.notes.length}{' '}
                  {session.notes.length === 1 ? 'note' : 'notes'}.
                </>
              ) : (
                <>
                  It will be logged as{' '}
                  <span className="text-brand-charcoal font-medium">
                    {Math.floor(elapsedMinutes / 60)}h {elapsedMinutes % 60}m
                  </span>{' '}
                  with {session.notes.length}{' '}
                  {session.notes.length === 1 ? 'note' : 'notes'}.
                </>
              )}
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmEndOpen(false)}
                disabled={endSession.isPending}
                className="min-h-[44px] flex-1 rounded-lg bg-brand-white px-4 py-2 text-sm font-semibold text-brand-slate ring-1 ring-brand-steel hover:bg-brand-steel active:scale-95 transition-transform"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmEnd}
                disabled={endSession.isPending}
                className={
                  'min-h-[44px] flex-1 rounded-lg px-4 py-2 text-sm font-semibold text-brand-white active:scale-95 transition-transform disabled:opacity-50 ' +
                  (isFlatActive
                    ? 'bg-brand-blue hover:bg-[#0067BB]'
                    : 'bg-red-600 hover:bg-red-700')
                }
              >
                {endSession.isPending ? 'Logging…' : isFlatActive ? 'Submit' : 'End & log'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmCancelOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-session-title"
          className="fixed inset-0 z-50 flex items-end justify-center bg-brand-charcoal/60 p-4 sm:items-center"
          onClick={() => setConfirmCancelOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-brand-white p-6 ring-1 ring-brand-steel shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="cancel-session-title" className="text-lg font-semibold text-brand-charcoal">
              Discard this session?
            </h2>
            <p className="mt-2 text-sm text-brand-slate">
              Nothing will be logged. The timer and {session.notes.length}{' '}
              {session.notes.length === 1 ? 'note' : 'notes'} will be deleted.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmCancelOpen(false)}
                disabled={cancelSession.isPending}
                className="min-h-[44px] flex-1 rounded-lg bg-brand-white px-4 py-2 text-sm font-semibold text-brand-slate ring-1 ring-brand-steel hover:bg-brand-steel active:scale-95 transition-transform"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={confirmCancel}
                disabled={cancelSession.isPending}
                className="min-h-[44px] flex-1 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-brand-white hover:bg-amber-700 active:scale-95 transition-transform disabled:opacity-50"
              >
                {cancelSession.isPending ? 'Discarding…' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
